import React, { useState, useEffect } from 'react'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Chip from '@material-ui/core/Chip'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import Typography from '@material-ui/core/Typography'
import BootstrapTable from 'react-bootstrap-table-next'
import Grid from '@material-ui/core/Grid'
import Loader from 'components/general/Loader'
import AdminAPI from 'config/api/AdminAPI'
import EnergyAPI from 'config/api/EnergyAPI'
import { onError } from 'config/lib/errorLib'
import filterFactory, { textFilter, selectFilter, numberFilter } from 'react-bootstrap-table2-filter'
import AddPaymentModal from 'components/admin/AddPaymentModal'
import PaymentModal from 'components/admin/PaymentModal'
import XeroSyncDialog from 'components/admin/XeroSyncDialog'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import moment from 'moment-timezone'

const useStyles = makeStyles(componentStyles)

function PaymentsTable(props) {
    const classes = { ...useStyles() }
    const api = new AdminAPI()
    const eApi = new EnergyAPI()

    // rows with a retry charge in flight, keyed companyId|date (rows have no stable keyField)
    const [chargingRows, setChargingRows] = useState({})
    const [alert, setAlert] = useState(null)
    // paid FS rows selected for Sync to Xero, keyed companyId|date
    const [xeroSelected, setXeroSelected] = useState({})
    const [showXeroSync, setShowXeroSync] = useState(false)
    const canSyncXero = props.product === 'Financial'

    function toggleXeroSelected(row, checked) {
        const key = row.companyId + '|' + row.date
        setXeroSelected(prev => {
            const next = { ...prev }
            if (checked) {
                next[key] = row
            } else {
                delete next[key]
            }
            return next
        })
    }

    function confirmRetryCharge(payment) {
        const due = (payment.total - (payment.amountPaid || 0)).toFixed(2)
        setAlert(
            <ReactBSAlert
                warning
                showCancel
                style={{ display: 'block', marginTop: '-100px' }}
                title='Retry charge?'
                onConfirm={() => { setAlert(null); retryCharge(payment) }}
                onCancel={() => setAlert(null)}
                cancelBtnCssClass='btn-secondary'
                confirmBtnBsStyle='primary'
                cancelBtnText='Cancel'
                confirmBtnText={'Charge €' + due}
                btnSize=''
            >
                This will charge €{due} to {payment.companyName}'s saved payment method. No new invoice will be generated.
            </ReactBSAlert>
        )
    }

    function showChargeResultAlert(title, message, danger = true) {
        setAlert(
            <ReactBSAlert
                danger={danger}
                warning={!danger}
                style={{ display: 'block', marginTop: '-100px' }}
                title={title}
                onConfirm={() => setAlert(null)}
                confirmBtnBsStyle='primary'
                confirmBtnText='OK'
                btnSize=''
            >
                {message}
            </ReactBSAlert>
        )
    }

    async function retryCharge(payment) {
        const key = payment.companyId + '|' + payment.date
        setChargingRows(prev => ({ ...prev, [key]: true }))
        try {
            const response = props.product === 'Financial' ? await api.retryCharge(payment) : await eApi.retryCharge(payment)
            console.log('retry charge response: ', response)

            const stripeCharge = (response.steps && response.steps.stripeCharge) || {}
            if (response.success && stripeCharge.status === 'succeeded') {
                if (response.payment) {
                    props.updatePayment(response.payment)
                }
                props.showSuccessAlert('Payment charged successfully!')
            }
            else if (response.success && stripeCharge.status === 'processing') {
                if (response.payment) {
                    props.updatePayment(response.payment)
                }
                showChargeResultAlert('Charge processing', 'The charge is processing (deferred payment method) — it will need to be marked paid manually once it settles.', false)
            }
            else {
                showChargeResultAlert('Charge failed', stripeCharge.error || 'Unknown error')
                props.refreshPayments()
            }
        } catch(e) {
            onError(e)
        } finally {
            setChargingRows(prev => {
                const next = { ...prev }
                delete next[key]
                return next
            })
        }
    }

    // async function chargeCompany(payment) {
    //     console.log('charge company for: ', payment)
    //     try {   
    //         let updatedPayment = await api.chargeCompany(payment)
    //         console.log('payment: ', updatedPayment)

    //         props.updatePayment(updatedPayment)
    //         props.showSuccessAlert('Payment succeeded!')
    //     } catch(e) {
    //         onError(e)
    //     }
    // }

    async function sendPaymentLink(payment) {
        console.log('send link for: ', payment)
        try {   
            let companyDetails = await api.getCompanyDetails(payment.companyId)
            console.log('company details: ', companyDetails)

            if (companyDetails.billingEmail) {
                let updatedPayment = await api.sendPaymentLink(payment)
                console.log('payment: ', updatedPayment)

                props.updatePayment(updatedPayment)
                props.showSuccessAlert('Payment link sent!')
            }
            else {
                alert('Please add billing email in companies screen')
            }
        } catch(e) {
            onError(e)
        }
    }

    async function sendPaymentReminder(payment) {
        console.log('send reminder for: ', payment)
        try {   
            let companyDetails = await api.getCompanyDetails(payment.companyId)
            console.log('company details: ', companyDetails)

            if (companyDetails.billingEmail) {
                let response = await api.sendPaymentReminder(payment)
                console.log('response: ', response)

                //props.updatePayment(updatedPayment)
                props.showSuccessAlert('Payment reminder sent!')
            }
            else {
                alert('Please add billing email in companies screen')
            }
        } catch(e) {
            onError(e)
        }
    }

    let payments = []

    for (let i = 0; i < props.payments.length; i++) {
        if (props.payments[i+1]) {
            if (props.payments[i].date !== props.payments[i+1].date) {
                payments.push(props.payments[i])
                console.log('insert blank')
                payments.push({})
            }   
            else {
                payments.push(props.payments[i])
            }
        }
        else {
            payments.push(props.payments[i])
        }
    }
    console.log('sorted: ', payments)
    let netSum = 0
    let grossSum = 0
    for (let payment of props.payments) {
        netSum += parseFloat(payment.subtotal)
        grossSum += parseFloat(payment.total)
    }
    console.log('net sum: ', netSum)
    console.log('gross sum: ', grossSum)

    const [payment, setPayment] = useState(null)
    const [showPaymentModal, setShowPaymentModal] = useState(false)
    const [showAddPaymentModal, setShowAddPaymentModal] = useState(false)
    const [loading, setLoading] = useState(false)

    const columns = [
        ...(canSyncXero ? [{
            dataField: 'xeroSelect',
            text: '',
            sort: false,
            formatter: (cell, row) => {
                if (row.status !== 'paid') return <></>
                const key = row.companyId + '|' + row.date
                return (
                    <Checkbox
                        size='small'
                        checked={!!xeroSelected[key]}
                        onChange={(e) => toggleXeroSelected(row, e.target.checked)}
                        title={row.paymentIntentId ? 'Already synced: ' + row.paymentIntentId : 'Select for Sync to Xero'}
                    />
                )
            },
            formatExtraData: xeroSelected,
            footer: ''
        }] : []),
        {
            dataField: 'date',
            text: 'Date',
            //sort: true,
            //filter: textFilter(),
            formatter: (cell, row) => {
                return <>{row.hasOwnProperty('date') ? moment(row.date).format('DD/MM') : '- Week -'}</>
            },
            footer: 'Total'
        },
        // income protection
        {
            dataField: 'company',
            text: 'Company',
            sort: true,
            //filter: numberFilter(),
            formatter: (cell, row) => {
                return (
                    <Box className='hover-text primary-text' onClick={() => openPaymentModal(row)}>
                        {row.hasOwnProperty('companyName') ? row.companyName : ''}
                        {row.billingName && (
                            <Chip
                                label={`Billed to ${row.billingName}`}
                                size="small"
                                style={{ marginLeft: '8px', backgroundColor: '#11cdef', color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: '20px' }}
                            />
                        )}
                    </Box>
                )
            },
            footer: ''
        },
        {
            dataField: 'description',
            text: 'Description',
            sort: true,
            //filter: numberFilter(),
            formatter: (cell, row) => {
                if (!row.hasOwnProperty('allocation')) {
                    return <>{row.hasOwnProperty('billingMethod') ? row.billingPeriod : ''}</>
                }
                const allocationBased = Object.values(row.allocation).some(v => v.isAllocationBased || v.hasOwnProperty('actualDelivered'));
                const billingLabel = allocationBased ? 'Allocation' : 'Actual';
                const billingStyle = allocationBased
                    ? { bg: '#cce5ff', color: '#004085' }
                    : { bg: '#d4edda', color: '#155724' };
                return (
                    <>
                        {getDescription(row.allocation)}
                        {props.product !== 'Financial' && row.date >= '2026-04-10' && (
                            <>
                                {' '}
                                <Chip
                                    label={billingLabel}
                                    size="small"
                                    style={{
                                        backgroundColor: billingStyle.bg,
                                        color: billingStyle.color,
                                        fontWeight: 600,
                                        fontSize: '0.65rem',
                                        height: '20px',
                                    }}
                                />
                            </>
                        )}
                    </>
                );
            },
            footer: ''
        },
        // {
        //     dataField: 'billingAccount',
        //     text: 'Billing Ac',
        //     sort: true,
        //     //filter: numberFilter(),
        //     formatter: (cell, row) => {
        //         return <>{row.hasOwnProperty('billingAccount') ? row.billingAccount : ''}</>
        //     },
        //     footer: ''
        // },
        {
            dataField: 'billingPeriod',
            text: 'Period',
            //sort: true,
            //filter: textFilter(),
            formatter: (cell, row) => {
                return <>{row.hasOwnProperty('billingPeriod') ? row.billingPeriod : ''}</>
            },
            footer: ''
        },
        {
            dataField: 'status',
            text: 'Status',
            //sort: true,
            //filter: textFilter(),
            formatter: (cell, row) => {
                // Prefer new status string
                if (typeof row.status === 'string') {
                    // Normalize capitalization for known statuses
                    const known = ['pending','processing','invoiced','paid'];
                    if (known.includes(row.status)) {
                        const label = row.status.charAt(0).toUpperCase() + row.status.slice(1);
                        if (row.lastChargeError && row.status !== 'paid') {
                            return <span title={'Last charge attempt failed: ' + row.lastChargeError}>{label} ⚠</span>;
                        }
                        return <>{label}</>;
                    }
                    // fallback for any other status
                    return <>{row.status}</>;
                }
                // Fallback for old invoices
                if (row.paid === true) {
                    return <>Paid</>;
                }
                if (row.invoiced === true) {
                    return <>Invoiced</>;
                }
                return <>-</>;
            },
            footer: ''
        },
        // {
        //     dataField: 'subtotal',
        //     text: 'Net (€)',
        //     //sort: true,
        //     //filter: textFilter(),
        //     formatter: (cell, row) => {
        //         return <>{row.hasOwnProperty('subtotal') ? '€' + row.subtotal.toFixed(2) : ''}</>
        //     },
        //     footer: '€' + netSum.toFixed(2)
        // },
        {
            dataField: 'total',
            text: 'Total (€)',
            //sort: true,
            //filter: textFilter(),
            formatter: (cell, row) => {
                return <>{row.hasOwnProperty('total') ? '€' + row.total.toFixed(2) : ''}</>
            },
            footer: '€' + grossSum.toFixed(2)
        },
        {
            dataField: 'due',
            text: 'Due (€)',
            //sort: true,
            //filter: textFilter(),
            formatter: (cell, row) => {
                // Treat 'paid' as settled; all others (including processing) show remaining due
                const isPaid = row.paid === true || row.status === 'paid';
                return <>{isPaid ? '€0.00' : (row.hasOwnProperty('total') ? '€' + (row.total - (row.amountPaid || 0)).toFixed(2) : '')}</>
            },
            footer: ''
        },
        {
            dataField: 'lastEmail',
            text: 'Last Email',
            sort: false,
            formatter: (cell, row) => {
                return <Box>{row.hasOwnProperty('date') ? ((row.emails && row.emails.length > 0) ? moment(row.emails[row.emails.length - 1].timestamp).format('DD/MM HH:mm') : '-') : ''}</Box>
            },
            footer: ''
        },
        {
            dataField: 'companyId',
            text: 'Action',
            sort: false,
            formatter: (cell, row) => {
                const chargeKey = row.companyId + '|' + row.date
                return (
                    <>
                        {row.status === 'invoiced' && (
                            <Button
                                color='primary'
                                variant='contained'
                                size='small'
                                disabled={!!chargingRows[chargeKey]}
                                onClick={() => confirmRetryCharge(row)}
                                style={{margin: '0rem', marginRight: '0.5rem'}}
                            >
                                {chargingRows[chargeKey] ? 'Charging…' : 'Retry Charge'}
                            </Button>
                        )}
                        {(row.paid === false && row.billingAccount === 'SS' && row.billingMethod === 'INV' && row.status !== 'paid') ? (
                            <>
                                {/*
                                <Button 
                                    color='primary'
                                    variant='contained'
                                    size='small'
                                    onClick={() => chargeCompany(row)}
                                    style={{margin: '0.5rem'}}
                                >
                                    Charge
                                </Button>
                                */}
                                {row.invoiced ? (
                                    <Button 
                                        color='secondary'
                                        variant='contained'
                                        size='small'
                                        onClick={() => sendPaymentReminder(row)}
                                        style={{margin: '0rem'}}
                                    >
                                        Send Reminder
                                    </Button>
                                ) : (
                                    <Button 
                                        color='secondary'
                                        variant='contained'
                                        size='small'
                                        onClick={() => sendPaymentLink(row)}
                                        style={{margin: '0rem'}}
                                    >
                                        Send Link
                                    </Button>
                                )}
                            </>
                        ) : (
                            <></>
                        )}
                    </>
                )
            },
            footer: ''
        },
    ]

    function getDescription(allocations) {
        let description = ''

        if (allocations) {
            for (let [product, values] of Object.entries(allocations)) {
                let shortened = '';
                if (product === 'life-insurance') {
                    shortened = 'LC';
                }
                if (product === 'income-protection') {
                    shortened = 'IP';
                }
                if (product === 'pensions') {
                    shortened = 'PEN';
                }
                if (product === 'mortgage-protection') {
                    shortened = 'MP';
                }
                if (product === 'health-insurance') {
                    shortened = 'HI';
                }
                if (product === 'home-insurance') {
                    shortened = 'HOME';
                }
                if (product === 'car-insurance') {
                    shortened = 'CAR';
                }
                if (product === 'business-insurance') {
                    shortened = 'BUS';
                }
                if (product === 'gadget-insurance') {
                    shortened = 'GAD';
                }
                if (product === 'pet-insurance') {
                    shortened = 'PET';
                }
                if (product === 'travel-insurance') {
                    shortened = 'TRAV';
                }
                if (product === 'start-pensions') {
                    shortened = 'SP';
                }
                if (product === 'Solar') {
                    shortened = 'Solar';
                }
                if (product === 'Insulation') {
                    shortened = 'INS';
                }
                if (product === 'EV Charger') {
                    shortened = 'EV';
                }
                if (product === 'Boiler') {
                    shortened = 'BOIL';
                }
                if (product === 'Heat Pump') {
                    shortened = 'HP';
                }
                if (parseInt(values.allocation) > 0) {
                    description += shortened + 'x' + values.allocation + ' ';
                }
            }
        }
        else {
            description = 'Topup'
        }

        return description
    }

    function openPaymentModal(payment) {
        setPayment(payment)
        setShowPaymentModal(true)
    }

    function closePaymentModal() {
        setShowPaymentModal(false)
        setPayment(null)
    }

    return (
        <Card>
            {alert}
            {!loading ? (
                <>
                    <CardHeader
                        subheader={
                            <>
                                <Box display='flex' style={{justifyContent: 'space-between'}}>
                                    <Box>
                                        <Box
                                            component={Typography}
                                            variant='h2'
                                            marginBottom='0!important'
                                        >
                                            <Box component='span'>Invoices</Box>
                                        </Box>
                                        {/* <Box
                                            component='p'
                                            fontSize='.875rem'
                                            marginBottom='0'
                                            marginTop='0'
                                            lineHeight='1.7'
                                            fontWeight='300'
                                        >
                                            Invoices are created automatically on Saturday at 00:00 for the previous week
                                        </Box> */}
                                    </Box>
                                    {canSyncXero && (
                                        <Box>
                                            <Button
                                                onClick={() => setShowXeroSync(true)}
                                                color='primary'
                                                variant='contained'
                                                size='small'
                                                disabled={Object.keys(xeroSelected).length === 0 || Object.keys(xeroSelected).length > 25}
                                                title={Object.keys(xeroSelected).length > 25 ? 'At most 25 payments can be synced at once' : ''}
                                            >
                                                Sync to Xero{Object.keys(xeroSelected).length > 0 ? ' (' + Object.keys(xeroSelected).length + ')' : ''}
                                            </Button>
                                        </Box>
                                    )}
                                    {/* <Box>
                                        <Button
                                            component={Box}
                                            onClick={() => setShowAddPaymentModal(true)}
                                            color='primary'
                                            variant='contained'
                                            size='small'
                                        >
                                            Add Invoice
                                        </Button>
                                    </Box> */}
                                </Box>
                            </>
                        }
                        classes={{ root: classes.cardHeaderRoot }}
                    ></CardHeader>

                    <div className='table-container'>
                        <BootstrapTable 
                            keyField='brokerName' 
                            data={ payments } 
                            columns={ columns } 
                            filter={ filterFactory() } 
                            noDataIndication={<Box>No payments</Box>}
                        />
                    </div>

                    {/* {showAddPaymentModal &&
                        <AddPaymentModal 
                            isOpen={showAddPaymentModal}
                            close={() => setShowAddPaymentModal(false)}
                            showSuccessAlert={(text) => props.showSuccessAlert(text)}
                            refreshPayments={() => props.refreshPayments()}
                            companies={props.companies}
                            product={props.product}
                        />
                    } */}

                    {showXeroSync &&
                        <XeroSyncDialog
                            isOpen={showXeroSync}
                            close={() => { setShowXeroSync(false); setXeroSelected({}) }}
                            payments={Object.values(xeroSelected)}
                            onSynced={() => props.refreshPayments()}
                        />
                    }

                    {showPaymentModal &&
                        <PaymentModal 
                            isOpen={showPaymentModal}
                            close={() => closePaymentModal()}
                            payment={payment}
                            updatePayment={(payment) => props.updatePayment(payment)}
                            refreshPayments={() => props.refreshPayments()}
                            showSuccessAlert={(text) => props.showSuccessAlert(text)}
                            //companies={props.companies}
                            product={props.product}
                        />
                    }
                </>
            ) : (
                <Grid container justifyContent='flex-start'>
                    <Grid item xs={12}>
                        <Box display='flex' justifyContent='center' alignItems='center' style={{height: '20vh'}}>
                            <Loader />
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Card>
    );
}

export default PaymentsTable